'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';

import { WebsiteInputStyle, WebsiteStyle } from '@cineamo/api/models/website-configuration/WebsiteStyle.types';

import { apiResponseHandler, apiErrorStatus } from '@cineamo/api/helpers/api-response-helper/api-response-helper';
import {
    deleteCartGiftCardByCode,
    getCartGiftCards,
    setCartGiftCardByCode
} from '@cineamo/api/resources/cart-gift-card/cart-gift-card-api';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { ButtonStyle } from '@cineamo/ui/src/button/ButtonStyle.enum';
import Button from '@cineamo/ui/src/button/NewButton';
import { IconClose16 } from '@cineamo/ui/src/icons/ic-close';
import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';

import { useCartProvider } from '@cineamo/composables/src/providers/cartProvider';

/** The API rejects a cart whose amount due is above zero but below this (cents). */
const MINIMUM_AMOUNT_DUE = 50;

const ERROR_KEY_BY_STATUS: Record<number, string> = {
    400: 'invalid',
    403: 'not-available',
    404: 'not-found',
    410: 'expired'
};

/**
 * Redeems a gift card against the cart. A cart holds one card at a time (the ticket system takes one), so the
 * code field gives way to the attached card, which has to be removed before another can be entered.
 */
const GiftCardRedemption: React.FC<{
    className?: string;
    websiteStyle?: WebsiteStyle & WebsiteInputStyle;
}> = ({ className, websiteStyle }) => {
    const t = useTranslations('tickets');
    const formatter = useFormat();
    const queryClient = useQueryClient();
    const { cartUuid, reloadCart, giftCardDeduction, amountDue, currency } = useCartProvider();
    const [isOpen, setIsOpen] = useState(false);

    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const queryKey = ['cart-gift-cards', `cartUuid-${cartUuid}`];
    const { data: giftCard, isLoading } = useQuery({
        queryKey,
        queryFn: async () =>
            (await getCartGiftCards(cartUuid))?._embedded?.['cart-gift-cards']?.[0]?._embedded?.giftCard ?? null,
        enabled: !!cartUuid
    });

    const refresh = async () => {
        await Promise.all([queryClient.invalidateQueries({ queryKey }), reloadCart()]);
    };

    const apply = async (event: React.FormEvent) => {
        event.preventDefault();
        const trimmedCode = code.trim();
        if (!trimmedCode || isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        setError(null);
        const response = await setCartGiftCardByCode(cartUuid, trimmedCode);
        await apiResponseHandler(
            response,
            async () => {
                setCode('');
                await refresh();
            },
            (errorResponse) => {
                setError(t(`gift-card.error.${ERROR_KEY_BY_STATUS[apiErrorStatus(errorResponse) ?? 0] ?? 'generic'}`));
            }
        );
        setIsSubmitting(false);
    };

    const remove = async () => {
        if (!giftCard || isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        setError(null);
        const response = await deleteCartGiftCardByCode(cartUuid, giftCard.code);
        if (response === true) {
            await refresh();
        } else {
            setError(t('gift-card.error.generic'));
        }
        setIsSubmitting(false);
    };

    const isAmountDueTooLow = giftCardDeduction > 0 && amountDue > 0 && amountDue < MINIMUM_AMOUNT_DUE;

    // Folded to a link until asked for: an empty field in front of paying distracts everyone who has no gift card.
    if (!giftCard && !isOpen) {
        return (
            <button
                type="button"
                onClick={() => setIsOpen(true)}
                className={cn(
                    'self-start text-body-bold-14 text-[var(--secondary-color,var(--primary-color))] outline-none focus-visible:underline',
                    className
                )}>
                {t('gift-card.redeem')}
            </button>
        );
    }

    return (
        <section
            aria-label={t('gift-card.title')}
            className={cn('flex flex-col space-y-8', className)}
            style={
                {
                    '--input-background-color': websiteStyle?.inputBackgroundColor,
                    '--input-text-color': websiteStyle?.inputTextColor,
                    '--input-placeholder-color': websiteStyle?.inputPlaceholderColor
                } as React.CSSProperties
            }>
            <div className="flex items-center justify-between gap-16">
                <h2 className="text-body-bold-16">{t('gift-card.title')}</h2>
                {!giftCard && (
                    <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        className="text-body-bold-14 text-[var(--secondary-color,var(--primary-color))] outline-none focus-visible:underline">
                        {t('gift-card.close')}
                    </button>
                )}
            </div>

            {giftCard ? (
                <div className="flex items-center justify-between gap-16 rounded-4 bg-[rgba(var(--surface-color-rgb),0.1)] p-8">
                    <div className="flex min-w-0 flex-col">
                        <span className="truncate text-body-bold-14">{giftCard.code}</span>
                        {giftCardDeduction > 0 && (
                            <span className="text-body-14">
                                {t('gift-card.applied-deduction')}{' '}
                                {formatter.number.formatCurrency(-giftCardDeduction / 100, currency)}
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={remove}
                        disabled={isSubmitting}
                        aria-label={t('gift-card.remove')}
                        title={t('gift-card.remove')}
                        className={cn(
                            'flex size-32 shrink-0 items-center justify-center rounded-4',
                            'outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)]',
                            'disabled:cursor-not-allowed disabled:opacity-50'
                        )}>
                        {isSubmitting ? <IconLoading16 className="animate-spin" /> : <IconClose16 />}
                    </button>
                </div>
            ) : (
                <form
                    onSubmit={apply}
                    className="flex items-end gap-8">
                    <div className="flex min-w-0 grow flex-col space-y-4">
                        <label
                            htmlFor="gift-card-code"
                            className="text-body-12 opacity-50">
                            {t('gift-card.code-label')}
                        </label>
                        <input
                            id="gift-card-code"
                            type="text"
                            autoComplete="off"
                            autoCapitalize="characters"
                            spellCheck={false}
                            value={code}
                            onChange={(event) => setCode(event.target.value)}
                            disabled={isSubmitting || isLoading}
                            aria-invalid={!!error}
                            aria-describedby={error ? 'gift-card-error' : undefined}
                            className="h-[48px] w-full rounded-[4px] border-none bg-[var(--input-background-color)] px-[16px] text-body-16 text-[var(--input-text-color)] placeholder:text-[var(--input-placeholder-color)] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[var(--focus-ring-color)]"
                        />
                    </div>
                    <Button
                        type="submit"
                        style={ButtonStyle.default}
                        className="min-h-48"
                        disabled={!code.trim() || isSubmitting || isLoading || !cartUuid}
                        leadingIcon={isSubmitting && <IconLoading16 className="animate-spin" />}>
                        {t('gift-card.apply')}
                    </Button>
                </form>
            )}

            {error && (
                <p
                    id="gift-card-error"
                    role="alert"
                    className="text-body-14 text-red">
                    {error}
                </p>
            )}
            {isAmountDueTooLow && (
                <p
                    role="alert"
                    className="text-body-14 text-red">
                    {t('gift-card.amount-due-too-low')}
                </p>
            )}
        </section>
    );
};

export default GiftCardRedemption;
export { MINIMUM_AMOUNT_DUE };
